import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, GitCompareArrows, ArrowRight } from "lucide-react";
import { homesCompare, homesFeaturedCities, homesNeighborhood } from "@/lib/api";

const fmtMoney = (n) => (typeof n === "number" ? `$${n.toLocaleString()}` : "—");
const fmtNum = (n) => (typeof n === "number" ? n.toLocaleString() : "—");
const fmtPct = (n) => (typeof n === "number" ? `${n}%` : "—");

const ROWS = [
  { key: "population", label: "Population", fmt: fmtNum },
  { key: "median_sale_price", label: "Median home price", fmt: fmtMoney, better: "low" },
  { key: "median_rent", label: "Median rent", fmt: (n) => (n ? `${fmtMoney(n)}/mo` : "—"), better: "low" },
  { key: "avg_price_per_sqft", label: "Price / sq ft", fmt: fmtMoney, better: "low" },
  { key: "for_sale", label: "Homes for sale", fmt: fmtNum, better: "high" },
  { key: "for_rent", label: "Rentals available", fmt: fmtNum, better: "high" },
];

// Census-grounded demographic rows (read from the neighborhood endpoint).
const CENSUS_ROWS = [
  { key: "median_household_income", label: "Median household income", fmt: fmtMoney, better: "high" },
  { key: "median_home_value", label: "Median home value (Census)", fmt: fmtMoney, better: "low" },
  { key: "median_gross_rent", label: "Median gross rent (Census)", fmt: (n) => (n ? `${fmtMoney(n)}/mo` : "—"), better: "low" },
  { key: "owner_pct", label: "Owner-occupied", fmt: fmtPct, better: "high" },
];

export default function CityCompare() {
  const [params, setParams] = useSearchParams();
  const [cities, setCities] = useState([]);
  const [a, setA] = useState(params.get("a") || "");
  const [b, setB] = useState(params.get("b") || "");
  const [data, setData] = useState(null);
  const [nb, setNb] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); homesFeaturedCities(60).then((d) => setCities(d.cities || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!a || !b || a === b) { setData(null); setNb(null); setError(false); return; }
    setLoading(true); setError(false); setNb(null);
    setParams({ a, b });
    homesCompare(a, b).then(setData).catch(() => { setData(null); setError(true); }).finally(() => setLoading(false));
    Promise.all([homesNeighborhood(a).catch(() => null), homesNeighborhood(b).catch(() => null)])
      .then(([na, nbb]) => setNb({ a: na?.census || {}, b: nbb?.census || {}, configured: !!(na?.census_configured) }));
  }, [a, b]); // eslint-disable-line

  const winner = (row, va, vb) => {
    if (!row.better || typeof va !== "number" || typeof vb !== "number" || va === vb) return null;
    const aWins = row.better === "low" ? va < vb : va > vb;
    return aWins ? "a" : "b";
  };

  return (
    <div data-testid="city-compare">
      <section className="bg-[#0F172A] text-white">
        <div className="mx-auto max-w-[1100px] px-4 py-12">
          <Link to="/homes/relocation" className="mb-4 inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white">← Relocation guides</Link>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#93B4FF]">
            <GitCompareArrows className="h-3.5 w-3.5" /> Compare Cities
          </span>
          <h1 className="mt-3 text-4xl font-black md:text-5xl">Two cities, side by side</h1>
          <p className="mt-2 max-w-2xl text-white/70">Compare housing costs, inventory and character to decide where to move.</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-[1fr_auto_1fr]">
            <CityPicker value={a} onChange={setA} cities={cities} exclude={b} testid="compare-select-a" />
            <div className="hidden items-center justify-center text-white/40 sm:flex">vs</div>
            <CityPicker value={b} onChange={setB} cities={cities} exclude={a} testid="compare-select-b" />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1100px] px-4 py-10">
        {loading ? (
          <div className="flex min-h-[30vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /></div>
        ) : error ? (
          <p className="text-center text-slate-500" data-testid="compare-error">We couldn't load one of those places. Please pick two different cities from the lists above.</p>
        ) : !data ? (
          <p className="text-center text-slate-500">Pick two different cities above to compare them.</p>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="compare-table">
            <div className="grid grid-cols-3 border-b border-slate-200 bg-[#F8FAFC]">
              <div className="p-4 text-xs font-bold uppercase tracking-widest text-slate-400">Metric</div>
              {[data.a, data.b].map((c, i) => (
                <div key={i} className="p-4 text-center">
                  <div className="text-lg font-black text-[#0F172A]">{c.name}</div>
                  <div className="text-xs text-slate-500">{c.state_name}</div>
                </div>
              ))}
            </div>
            {ROWS.map((row) => {
              const va = data.a[row.key], vb = data.b[row.key];
              const w = winner(row, va, vb);
              return (
                <div key={row.key} className="grid grid-cols-3 border-b border-slate-100 last:border-0">
                  <div className="p-4 text-sm font-semibold text-slate-600">{row.label}</div>
                  <Cell v={row.fmt(va)} win={w === "a"} />
                  <Cell v={row.fmt(vb)} win={w === "b"} />
                </div>
              );
            })}
            {nb && (nb.configured || nb.a.median_household_income != null || nb.b.median_household_income != null || nb.a.owner_pct != null || nb.b.owner_pct != null) && (
              <>
                <div className="grid grid-cols-3 border-b border-slate-100 bg-[#F8FAFC]">
                  <div className="p-3 text-xs font-bold uppercase tracking-widest text-slate-400">
                    Demographics · {nb.configured ? "U.S. Census ACS" : "Estimate"}
                  </div>
                  <div /><div />
                </div>
                {CENSUS_ROWS.map((row) => {
                  const va = nb.a[row.key], vb = nb.b[row.key];
                  if (va == null && vb == null) return null;
                  const w = winner(row, va, vb);
                  return (
                    <div key={row.key} className="grid grid-cols-3 border-b border-slate-100" data-testid={`compare-census-${row.key}`}>
                      <div className="p-4 text-sm font-semibold text-slate-600">{row.label}</div>
                      <Cell v={row.fmt(va)} win={w === "a"} />
                      <Cell v={row.fmt(vb)} win={w === "b"} />
                    </div>
                  );
                })}
              </>
            )}
            <div className="grid grid-cols-3 bg-[#F8FAFC]">
              <div className="p-4 text-sm font-semibold text-slate-600">Known for</div>
              {[data.a, data.b].map((c, i) => (
                <div key={i} className="p-4">
                  <div className="flex flex-wrap justify-center gap-1.5">
                    {(c.known_for || []).slice(0, 4).map((k) => <span key={k} className="rounded-full bg-[#EEF2FF] px-2 py-0.5 text-xs font-medium text-[#2563EB]">{k}</span>)}
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 border-t border-slate-200">
              <div />
              {[data.a, data.b].map((c, i) => (
                <div key={i} className="p-4 text-center">
                  <Link to={`/homes/relocation/${c.geo_id}`} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#2563EB] hover:underline" data-testid={`compare-guide-${i}`}>
                    Moving to {c.name} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CityPicker({ value, onChange, cities, exclude, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-white/20 bg-white/10 px-4 py-3 font-semibold text-white outline-none [&>option]:text-[#0F172A]"
      data-testid={testid}>
      <option value="">Select a city…</option>
      {cities.filter((c) => c.geo_id !== exclude).map((c) => (
        <option key={c.geo_id} value={c.geo_id}>{`${c.name}, ${c.state_name}`}</option>
      ))}
    </select>
  );
}

const Cell = ({ v, win }) => (
  <div className={`p-4 text-center text-lg font-bold ${win ? "bg-emerald-50 text-emerald-700" : "text-[#0F172A]"}`}>{v}{win && <span className="ml-1 text-xs">✓</span>}</div>
);
